const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Tier 1: in-memory (instant, lost on page close)
const memoryCache = new Map<string, CacheEntry<unknown>>();

// Tier 2: sessionStorage (survives refresh, lost on tab close)
function storageKey(key: string): string {
  return `eep_cache_${key}`;
}

function hashKey(input: string): string {
  // Simple fast hash for SOQL queries → short cache keys
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
}

export function getCached<T>(query: string, ttl = DEFAULT_TTL_MS): T | null {
  const key = hashKey(query);
  const now = Date.now();

  // Check memory first
  const mem = memoryCache.get(key) as CacheEntry<T> | undefined;
  if (mem && now - mem.timestamp < ttl) {
    return mem.data;
  }

  // Fall back to sessionStorage
  try {
    const raw = sessionStorage.getItem(storageKey(key));
    if (raw) {
      const entry: CacheEntry<T> = JSON.parse(raw);
      if (now - entry.timestamp < ttl) {
        // Promote back to memory
        memoryCache.set(key, entry);
        return entry.data;
      }
      // Expired — clean up
      sessionStorage.removeItem(storageKey(key));
    }
  } catch {
    // Corrupted entry
  }

  return null;
}

export function setCache<T>(query: string, data: T): void {
  const key = hashKey(query);
  const entry: CacheEntry<T> = { data, timestamp: Date.now() };

  // Write to memory
  memoryCache.set(key, entry);

  // Write to sessionStorage (best-effort, may fail if quota exceeded)
  try {
    sessionStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    // Quota exceeded — memory cache still works
  }
}

export function invalidateCache(query: string): void {
  const key = hashKey(query);
  memoryCache.delete(key);
  try {
    sessionStorage.removeItem(storageKey(key));
  } catch {
    // ignore
  }
}

export function clearAllCache(): void {
  memoryCache.clear();
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith('eep_cache_')) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}
